import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AvatarState {
  url: string | null;
  loading: boolean;
  uploading: boolean;
}

export const useAvatar = () => {
  const { toast } = useToast();
  const [avatar, setAvatar] = useState<AvatarState>({
    url: null,
    loading: true,
    uploading: false
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setAvatar(prev => ({ ...prev, loading: true }));
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('user_id', user.id)
        .single();

      if (profile?.avatar_url) {
        setAvatar(prev => ({ 
          ...prev, 
          url: profile.avatar_url,
          loading: false 
        }));
      } else {
        setAvatar(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setAvatar(prev => ({ ...prev, loading: false }));
    }
  };

  const uploadAvatar = async (file: File): Promise<string | null> => {
    try {
      setAvatar(prev => ({ ...prev, uploading: true }));

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Para debug - vamos simular um usuário
        console.warn('User not authenticated - using mock mode');
        const mockUrl = URL.createObjectURL(file);
        setAvatar(prev => ({ 
          ...prev, 
          url: mockUrl,
          uploading: false 
        }));
        
        toast({
          title: "Foto atualizada! (Modo Demo)",
          description: "Sua foto foi atualizada localmente. Faça login para salvar permanentemente.",
        });
        
        return mockUrl;
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Math.random()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatarUrl = data.publicUrl;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        });

      if (updateError) throw updateError;

      // Update local state
      setAvatar(prev => ({ 
        ...prev, 
        url: avatarUrl,
        uploading: false 
      }));

      toast({
        title: "Foto atualizada!",
        description: "Sua foto de perfil foi atualizada com sucesso.",
      });

      return avatarUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      
      setAvatar(prev => ({ ...prev, uploading: false }));
      
      toast({
        title: "Erro no upload",
        description: "Não foi possível atualizar sua foto. Tente novamente.",
        variant: "destructive"
      });

      return null;
    }
  };

  const removeAvatar = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setAvatar(prev => ({ ...prev, url: null }));

      toast({
        title: "Foto removida",
        description: "Sua foto de perfil foi removida.",
      });
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível remover a foto.",
        variant: "destructive"
      });
    }
  };

  return {
    avatar,
    uploadAvatar,
    removeAvatar,
    refreshAvatar: loadProfile
  };
};